/**
 * The cancel offered during the ring, published so the composers can show it.
 *
 * ── WHY A STORE AND NOT A PROP ──────────────────────────────────────────────
 *
 * The wake word is mounted in `AuthedShell` and renders nothing. The two places
 * a cancel can be shown — the docked mobile bar and the desktop Mucka panel —
 * are elsewhere in the tree and share no ancestor with it that carries state.
 * Same reason `status.ts` exists, and the same shape: a module-level value with
 * subscribers, read through `useSyncExternalStore`.
 *
 * 🚨 Directive-free on purpose. The hook that reads it lives in its own
 * `"use client"` module; putting one here would make every export a throwing
 * proxy on the server (CLAUDE.md anti-pattern #18).
 *
 * ── AND WHY IT HOLDS A FUNCTION ─────────────────────────────────────────────
 *
 * A boolean would tell the composer to draw a button and leave it with no way
 * to press it. Handing over the callback keeps the whole cancel — the machine
 * event, the ring, ending a session that may already be connecting — inside the
 * hook that owns those things, and leaves the composer with one job: call it.
 */

/** The live cancel, or null when there is nothing to cancel. */
let pending: (() => void) | null = null;
const listeners = new Set<() => void>();

export function getPendingWakeCancel(): (() => void) | null {
  return pending;
}

/**
 * Publish (or withdraw) the cancel.
 *
 * Skips a no-op set: the hook calls this from an effect that runs on every
 * handoff state change, and republishing an unchanged null would re-render both
 * composers for nothing.
 */
export function setPendingWakeCancel(next: (() => void) | null): void {
  if (pending === next) return;
  pending = next;
  for (const l of listeners) l();
}

export function subscribePendingWakeCancel(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
